import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Activity, TrendingUp, Database, Settings, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';





const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeCustomers: 0,
    systemPerformance: 0,
    monthlyGrowth: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    database: 'healthy',
    server: 'healthy',
    integrations: 'healthy'
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch total users count
      const { count: usersCount, error: usersError } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true });

      if (usersError) throw usersError;

      // Fetch customer role ID first - use maybeSingle() to handle 0 rows gracefully
      const { data: customerRole, error: customerRoleError } = await supabase
        ?.from('system_roles')
        ?.select('id')
        ?.eq('name', 'customer')
        ?.maybeSingle();

      if (customerRoleError) throw customerRoleError;

      // Fetch active customers (users with customer role assignment)
      let customersCount = 0;
      if (customerRole?.id) {
        const { count, error: customersError } = await supabase
          ?.from('user_role_assignments')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('role_id', customerRole?.id);

        if (customersError) throw customersError;
        customersCount = count || 0;
      }

      // Fetch recent activities with role information (last 10 users with JOIN)
      // FIXED: Specify exact foreign key relationship to resolve ambiguity
      const { data: activities, error: activitiesError } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          created_at,
          user_role_assignments!user_role_assignments_user_id_fkey!inner(
            role_id,
            system_roles!inner(
              name,
              display_name
            )
          )
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (activitiesError) throw activitiesError;

      // Calculate monthly growth (simplified - compare current month registrations)
      const startOfMonth = new Date();
      startOfMonth?.setDate(1);
      startOfMonth?.setHours(0, 0, 0, 0);

      const { count: monthlyCount, error: monthlyError } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.gte('created_at', startOfMonth?.toISOString());

      if (monthlyError) throw monthlyError;

      setMetrics({
        totalUsers: usersCount || 0,
        activeCustomers: customersCount || 0,
        systemPerformance: 98.5, // Mock value - can be replaced with real metrics
        monthlyGrowth: monthlyCount || 0
      });

      // Transform activities data to match expected format
      const transformedActivities = activities?.map(activity => ({
        id: activity?.id,
        email: activity?.email,
        role: activity?.user_role_assignments?.[0]?.system_roles?.display_name || 'No Role',
        created_at: activity?.created_at
      })) || [];

      setRecentActivities(transformedActivities);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const KPICard = ({ title, value, icon: IconComponent, bgColor, trend }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
          {trend && (
            <p className="text-xs text-green-600 mt-2 flex items-center">
              <TrendingUp className="w-3 h-3 mr-1" />
              {trend}
            </p>
          )}
        </div>
        <div className={`${bgColor} p-3 rounded-lg`}>
          <IconComponent className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon: IconComponent, onClick }) => (
    <button
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all text-left w-full"
    >
      <div className="flex items-start space-x-3">
        <div className="bg-blue-50 p-2 rounded-lg">
          <IconComponent className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">{title}</h4>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 relative">
      <ComponentIdBadge
        id="pg-admin-dashboard-main"
        name="Admin Dashboard Main Page"
        description="Administrative oversight with system metrics and user management"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 relative">
          <ComponentIdBadge
            id="hdr-admin-dashboard-title"
            name="Admin Dashboard Header"
            description="Page title and description"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Comprehensive administrative oversight and system management</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 relative">
          <ComponentIdBadge
            id="cmp-admin-kpi-cards"
            name="Admin KPI Cards"
            description="System metrics and performance indicators"
          />
          <KPICard
            title="Total Users"
            value={metrics?.totalUsers}
            icon={Users}
            bgColor="bg-blue-600"
            trend="+12% this month"
          />
          <KPICard
            title="Active Customers"
            value={metrics?.activeCustomers}
            icon={Activity}
            bgColor="bg-green-600"
            trend="+8% this month"
          />
          <KPICard
            title="System Performance"
            value={`${metrics?.systemPerformance}%`}
            icon={Database}
            bgColor="bg-purple-600"
            trend="Optimal"
          />
          <KPICard
            title="Monthly Growth"
            value={metrics?.monthlyGrowth}
            icon={TrendingUp}
            bgColor="bg-orange-600"
            trend="New registrations"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activities */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Administrative Activities</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivities?.length > 0 ? (
                  recentActivities?.map((activity) => (
                    <div key={activity?.id} className="flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-0">
                      <div className="bg-blue-50 p-2 rounded-full">
                        <UserPlus className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">New user registration</p>
                        <p className="text-xs text-gray-600 mt-1">{activity?.email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Role: {activity?.role} • {new Date(activity.created_at)?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No recent activities</p>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions & System Health */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <QuickActionCard
                  title="User Management"
                  description="Manage staff and permissions"
                  icon={Users}
                  onClick={() => navigate('/user-management')}
                />
                <QuickActionCard
                  title="Customer Overview"
                  description="View customer database"
                  icon={Activity}
                  onClick={() => navigate('/contacts')}
                />
                <QuickActionCard
                  title="System Settings"
                  description="Configure platform settings"
                  icon={Settings}
                  onClick={() => navigate('/settings')}
                />
              </div>
            </div>

            {/* System Health */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">System Health</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Database</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    systemHealth?.database === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {systemHealth?.database === 'healthy' ? 'Healthy' : 'Issue'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Server Status</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    systemHealth?.server === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {systemHealth?.server === 'healthy' ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Integrations</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    systemHealth?.integrations === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {systemHealth?.integrations === 'healthy' ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;